/**
 * Resolve the post-login landing route for a user.
 *
 * Resolution order:
 *   1. Role-code override (ROLE_LANDING) — for roles where the natural
 *      visibleTabs[0] is NOT the right primary work surface (e.g.
 *      SERVICE_COORDINATOR sees Service Leads + Service Ops, but their
 *      day-to-day work is dispatching on Service Ops, so land them
 *      there directly).
 *   2. First tab in visibleTabs[] that has a mapped route in
 *      TAB_ROUTE_MAP — skips unknown tab strings so an unfamiliar
 *      tab name doesn't silently fall through to "/leads".
 *   3. Last-resort fallback "/dashboard".
 *
 * Historical context (do not regress):
 *   - Pre-2026-06-08 the App.js / LoginPage.jsx tabRouteMap was missing
 *     "service-leads" and "service-ops" entries. A Service Coordinator
 *     whose visibleTabs[0] was "service-leads" silently fell back to
 *     "/leads" — a page they couldn't even see in the navbar. That bug
 *     produced the "blank Leads page after login" report from CEO on
 *     2026-06-08. Adding the entries below + the SERVICE_COORDINATOR
 *     override fixes that incident.
 */

// Per-role explicit landing override. Keep this list small —
// only add a role here if visibleTabs[0] would land the user on
// the wrong primary work surface.
const ROLE_LANDING = {
  // CEO/CTO always land on the operational overview, even if
  // a backend ordering glitch returns "reports" or "settings"
  // first in visible_tabs[]. Locks the 2026-06-12 incident
  // (CEOs sent to /reports / /settings after login).
  CEO: '/dashboard',
  CTO: '/dashboard',
  SERVICE_COORDINATOR: '/service-ops',
  SERVICE_OPS_EXEC: '/service-ops',
};

// Tab name → app route. MUST be kept in sync with the route table
// in App.js. Missing entries cause the redirect to fall through to
// "/dashboard" (or whatever final fallback the caller uses).
const TAB_ROUTE_MAP = {
  'dashboard': '/dashboard',
  'leads': '/leads',
  'customers': '/customers',
  'inspections': '/inspections',
  'loans': '/loans',
  'ad-analytics': '/ad-analytics',
  'hr': '/hr',
  'finance': '/finance',
  'settings': '/settings',
  // NOTE: 'reports' deliberately NOT mapped — there is no /reports
  // route in App.js, so mapping it would send CEOs to a blank page.
  // If a Reports dashboard is built later, add `'reports': '/reports'`
  // here AND register the route in App.js.
  // Service module — was missing pre-2026-06-08, caused the
  // SERVICE_COORDINATOR landing-page bug.
  'service-leads': '/service-leads',
  'service-ops': '/service-ops',
  'service-packages': '/service-packages',
  'dealers': '/dealers',
  'mfcwl': '/mfcwl',
  'system-monitor': '/system-monitor',
  'obd-sdk': '/obd-sdk',
};

export function pickLandingRoute({ user, visibleTabs }) {
  const safeTabs = Array.isArray(visibleTabs) ? visibleTabs : [];

  // 1. Role-specific override — only honour if the user actually has
  //    the target tab visible (defence against stale overrides).
  const roleCode = user?.role_code;
  if (roleCode && ROLE_LANDING[roleCode]) {
    const overridePath = ROLE_LANDING[roleCode];
    const overrideTab = overridePath.replace(/^\//, '');
    if (safeTabs.includes(overrideTab)) {
      return overridePath;
    }
  }

  // 2. First tab in visibleTabs[] with a mapped route.
  for (const tab of safeTabs) {
    if (TAB_ROUTE_MAP[tab]) {
      return TAB_ROUTE_MAP[tab];
    }
  }

  // 3. Last-resort fallback.
  return '/dashboard';
}
